// InterventionDetails.jsx
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import ConsumableSelectorModal from './ConsumableSelectorModal';
import SignaturePad from './SignaturePad';
import { supabase } from '../../../lib/supabase';

const InterventionDetails = ({ 
  appointment, 
  onComplete, 
  onUpdatePhotos, 
  onDeleteConsumable, 
  onUpdateSignature, 
  onSave,
  onUpdateConsumables,
  isSaving,
  uploading,
  user
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [checklist, setChecklist] = useState(appointment?.checklist || []);
  const [beforePhotos, setBeforePhotos] = useState(appointment?.photos?.before || []);
  const [afterPhotos, setAfterPhotos] = useState(appointment?.photos?.after || []);
  const [consumables, setConsumables] = useState([]);
  const [signature, setSignature] = useState(appointment?.signature || null);
  const [notes, setNotes] = useState(appointment?.notes || '');
  const [isCompleting, setIsCompleting] = useState(false);
  const [stockMap, setStockMap] = useState(new Map()); // Map pour stocker les quantités disponibles
  
  const [isConsumableModalOpen, setIsConsumableModalOpen] = useState(false);

  // Charger les stocks du technicien au chargement
  useEffect(() => {
    loadTechnicianStocks();
  }, [user]);

  // Charger les consommables de l'intervention avec leurs stocks
  useEffect(() => {
    if (appointment?.consumables && stockMap.size > 0) {
      // Enrichir les consommables avec leur stock disponible
      const enrichedConsumables = appointment.consumables.map(item => ({
        ...item,
        availableQuantity: stockMap.get(item.id) || 0
      }));
      setConsumables(enrichedConsumables);
    }
  }, [appointment, stockMap]);

  const loadTechnicianStocks = async () => {
    try {
      // Récupérer les quantités du technicien
      const { data: techConsumables, error } = await supabase
        ?.from('consumables_techniciens')
        .select('consumable, quantity')
        .eq('technicien', user?.id);
      
      if (error) {
        console.error("Erreur lors de la récupération des quantités:", error);
        return;
      }
      
      // Créer un Map pour un accès rapide ID -> quantité
      const quantityMap = new Map();
      techConsumables?.forEach(item => {
        quantityMap.set(item.consumable, item.quantity);
      });
      
      setStockMap(quantityMap);
      
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const steps = [
    { id: 'checklist', label: 'Checklist', icon: 'ClipboardCheck' },
    { id: 'photos', label: 'Photos', icon: 'Camera' },
    { id: 'consumables', label: 'Consommables', icon: 'Package' },
    { id: 'signature', label: 'Signature', icon: 'PenTool' }
  ];

  const handleChecklistToggle = (id) => {
    setChecklist(items =>
      items.map(item =>
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    );
  };

  const handlePhotoUpload = (type, event) => {
    const files = Array.from(event.target.files);
    
    if (type === 'before') {
      const newBeforePhotos = [...beforePhotos, ...files];
      setBeforePhotos(newBeforePhotos);
      if (onUpdatePhotos) {
        onUpdatePhotos({ before: newBeforePhotos, after: afterPhotos });
      }
    } else {
      const newAfterPhotos = [...afterPhotos, ...files];
      setAfterPhotos(newAfterPhotos);
      if (onUpdatePhotos) {
        onUpdatePhotos({ before: beforePhotos, after: newAfterPhotos });
      }
    }
    
    event.target.value = null;
  };

  const handleDeleteBeforePhoto = (index) => {
    const photoToDelete = beforePhotos[index];
    
    if (typeof photoToDelete === 'string' && photoToDelete.startsWith('blob:')) {
      URL.revokeObjectURL(photoToDelete);
    }
    
    const newBeforePhotos = beforePhotos.filter((_, i) => i !== index);
    setBeforePhotos(newBeforePhotos);
    
    if (onUpdatePhotos) {
      onUpdatePhotos({ before: newBeforePhotos, after: afterPhotos });
    }
  };

  const handleDeleteAfterPhoto = (index) => {
    const photoToDelete = afterPhotos[index];
    
    if (typeof photoToDelete === 'string' && photoToDelete.startsWith('blob:')) {
      URL.revokeObjectURL(photoToDelete);
    }
    
    const newAfterPhotos = afterPhotos.filter((_, i) => i !== index);
    setAfterPhotos(newAfterPhotos);
    
    if (onUpdatePhotos) {
      onUpdatePhotos({ before: beforePhotos, after: newAfterPhotos });
    }
  };

  const handleDeleteAllBeforePhotos = () => {
    beforePhotos.forEach(photo => {
      if (typeof photo === 'string' && photo.startsWith('blob:')) {
        URL.revokeObjectURL(photo);
      }
    });
    
    setBeforePhotos([]);
    if (onUpdatePhotos) {
      onUpdatePhotos({ before: [], after: afterPhotos });
    }
  };

  const handleDeleteAllAfterPhotos = () => {
    afterPhotos.forEach(photo => {
      if (typeof photo === 'string' && photo.startsWith('blob:')) {
        URL.revokeObjectURL(photo);
      }
    });
    
    setAfterPhotos([]);
    if (onUpdatePhotos) {
      onUpdatePhotos({ before: beforePhotos, after: [] });
    }
  };

  // Fonction pour gérer le changement de quantité avec validation
  const handleQuantityChange = (id, newValue, availableQuantity) => {
    const parsedValue = parseInt(newValue) || 0;
    
    // Ne pas dépasser la quantité disponible
    if (availableQuantity !== undefined && parsedValue > availableQuantity) {
      alert(`La quantité ne peut pas dépasser ${availableQuantity} (stock disponible)`);
      return;
    }
    
    setConsumables(items =>
      items.map(item =>
        item.id === id ? { ...item, quantity: parsedValue } : item
      )
    );
  };

  // Fonction pour incrémenter la quantité avec validation
  const handleIncrementQuantity = (id, availableQuantity) => {
    setConsumables(items =>
      items.map(item => {
        if (item.id === id) {
          const newQuantity = (item.quantity || 0) + 1;
          
          // Vérifier que ça ne dépasse pas la quantité disponible
          if (availableQuantity !== undefined && newQuantity > availableQuantity) {
            alert(`Stock disponible: ${availableQuantity}. Vous ne pouvez pas ajouter plus.`);
            return item;
          }
          
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  // Fonction pour décrémenter la quantité
  const handleDecrementQuantity = (id) => {
    setConsumables(items =>
      items.map(item => {
        if (item.id === id) {
          const newQuantity = Math.max((item.quantity || 0) - 1, 0);
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  const handleAddConsumables = (selectedConsumables) => {
    setConsumables(prev => {
      const newConsumables = [...prev];
      
      selectedConsumables.forEach(selectedItem => {
        const existingIndex = newConsumables.findIndex(item => item.id === selectedItem.id);
        const availableQty = stockMap.get(selectedItem.id) || 0;
        
        if (existingIndex >= 0) {
          // Incrémenter la quantité si l'item existe déjà
          const currentQty = newConsumables[existingIndex].quantity || 0;
          const newQty = currentQty + 1;
          
          // Vérifier la limite de stock
          if (newQty > availableQty) {
            alert(`Stock disponible pour ${selectedItem.name}: ${availableQty}. Vous ne pouvez pas ajouter plus.`);
            return;
          }
          
          newConsumables[existingIndex] = {
            ...newConsumables[existingIndex],
            quantity: newQty,
            availableQuantity: availableQty
          };
        } else {
          // Ajouter le nouvel item avec quantité 1
          if (1 > availableQty) {
            alert(`Stock disponible pour ${selectedItem.name}: ${availableQty}. Impossible d'ajouter.`);
            return;
          }
          
          newConsumables.push({
            ...selectedItem,
            quantity: 1,
            availableQuantity: availableQty
          });
        }
      });
      
      if (onUpdateConsumables) {
        onUpdateConsumables(newConsumables);
      }
      
      return newConsumables;
    });
    
    setIsConsumableModalOpen(false);
  };

  const handleRemoveConsumable = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce consommable ?')) {
      return;
    }
    
    try {
      setConsumables(prev => {
        const newConsumables = prev.filter(item => item.id !== id);
        
        if (onUpdateConsumables) {
          onUpdateConsumables(newConsumables);
        }
        
        return newConsumables;
      });

      if (onDeleteConsumable) {
        await onDeleteConsumable(id);
      }
      
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du consommable');
      
      if (onUpdateConsumables) {
        onUpdateConsumables(consumables);
      }
    }
  };

  const canComplete = () => {
    const requiredItems = checklist.filter(item => item.required);
    const requiredChecked = requiredItems.length > 0 
      ? requiredItems.every(item => item.checked)
      : true;
    
    return requiredChecked && beforePhotos.length > 0 && afterPhotos.length > 0 && signature;
  };

  const handleSignatureComplete = (signatureData) => {
    setSignature(signatureData);
    if (onUpdateSignature) {
      onUpdateSignature(signatureData);
    }
  };

  const getInterventionData = () => {
    return {
      id: appointment?.id,
      reference: appointment?.reference,
      checklist,
      photos: {
        before: beforePhotos,
        after: afterPhotos
      },
      consumables: consumables.filter(item => item.quantity > 0),
      signature,
      notes,
      updatedAt: new Date().toISOString()
    };
  };

  const handleSave = async () => {
    const interventionData = getInterventionData();
    if (onSave) {
      await onSave(interventionData);
    }
  };

  const handleCompleteIntervention = async () => {
    if (!canComplete()) return;
    
    setIsCompleting(true);
    try {
      const interventionData = {
        ...getInterventionData(),
        status: 'completed',
        completedAt: new Date().toISOString()
      };
      
      if (onComplete) {
        await onComplete(interventionData);
      }
    } catch (error) {
      console.error('Erreur lors de la complétion:', error);
    } finally {
      setIsCompleting(false);
    }
  };

  const nextStep = () => {
    handleSave();
 
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const prevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const getFileName = (photo) => {
    if (photo instanceof File) {
      return photo.name;
    } else if (typeof photo === 'string') {
      if (photo.includes('b-cdn.net')) {
        const parts = photo.split('/');
        return parts[parts.length - 1];
      } else if (photo.startsWith('blob:')) {
        return 'Photo temporaire';
      } else {
        return 'Photo';
      }
    }
    return 'Photo';
  };

  return (
    <div className="bg-card rounded-lg shadow-sm h-full flex flex-col">
      {/* Consumable Selector Modal */}
      <ConsumableSelectorModal
        isOpen={isConsumableModalOpen}
        onClose={() => setIsConsumableModalOpen(false)}
        onSelect={handleAddConsumables}
        existingConsumables={consumables}
        disabled={isSaving || isCompleting || uploading}
        user={user}
      />

      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Intervention en cours</h2>
            <p className="text-sm text-muted-foreground">{appointment?.customerName}</p>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Début: {new Date(appointment?.startTime || Date.now()).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>

        {/* Steps Navigation */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-2 overflow-x-auto flex-1">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    activeStep === index
                      ? 'bg-primary text-primary-foreground'
                      : index < activeStep
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                  disabled={uploading || isSaving || isCompleting}
                >
                  <Icon name={step.icon} size={16} />
                  <span>{step.label}</span>
                </button>
                {index < steps.length - 1 && (
                  <div className="w-4 h-0.5 bg-border mx-1"></div>
                )}
              </div>
            ))}
          </div>
          
          <div className="text-sm text-muted-foreground ml-4 whitespace-nowrap">
            Étape {activeStep + 1}/{steps.length}
          </div>
        </div>

        <div className="flex justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={prevStep}
            disabled={activeStep === 0 || isSaving || isCompleting || uploading}
            iconName="ChevronLeft"
          >
            Précédent
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={nextStep}
            disabled={activeStep === steps.length - 1 || isSaving || isCompleting || uploading}
            iconName="ChevronRight"
            iconPosition="right"
          >
            Suivant
          </Button>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4">
        {/* Checklist Step */}
        {activeStep === 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Points de contrôle</h3>
              <span className="text-sm text-muted-foreground">
                {checklist.filter(item => item.checked).length}/{checklist.length} complétés
              </span>
            </div>
            {checklist.length === 0 ? (
              <div className="text-center py-8 bg-muted/50 rounded-lg">
                <Icon name="ClipboardList" size={32} className="text-muted-foreground mb-2 mx-auto" />
                <p className="text-sm text-muted-foreground">Aucun point de contrôle défini</p>
              </div>
            ) : (
              checklist.map(item => (
                <div key={item.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <Checkbox
                    checked={item.checked || false}
                    onChange={() => handleChecklistToggle(item.id)}
                    className="mt-0.5"
                    disabled={isSaving || isCompleting || uploading}
                  />
                  <div className="flex-1">
                    <label className="text-sm font-medium text-foreground cursor-pointer">
                      {item.label}
                      {item.required && <span className="text-error ml-1">*</span>}
                    </label>
                  </div>
                </div>
              ))
            )}
            <div className="mt-4">
              <Input
                label="Notes d'intervention"
                type="text"
                placeholder="Ajoutez des notes sur l'intervention..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full"
                disabled={isSaving || isCompleting || uploading}
              />
            </div>
          </div>
        )}

        {/* Photos Step */}
        {activeStep === 1 && (
          <div className="space-y-6">
            {/* Photos Avant */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-foreground">Photos avant intervention</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {beforePhotos.length} photo{beforePhotos.length > 1 ? 's' : ''}
                    {beforePhotos.length > 0 && ' · Requis *'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {beforePhotos.length > 0 && (
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      iconName="Trash2"
                      onClick={handleDeleteAllBeforePhotos}
                      disabled={isSaving || isCompleting || uploading}
                    >
                      Tout supprimer
                    </Button>
                  )}
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handlePhotoUpload('before', e)}
                      className="hidden"
                      disabled={isSaving || isCompleting || uploading}
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      iconName="Plus" 
                      asChild 
                      disabled={isSaving || isCompleting || uploading}
                    >
                      <span>Ajouter</span>
                    </Button>
                  </label>
                </div>
              </div>
              
              {/* Grille des photos avant */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {beforePhotos.map((photo, index) => (
                  <div key={index} className="relative group aspect-square rounded-lg overflow-hidden bg-muted border border-border">
                    <Image
                      src={photo instanceof File ? URL.createObjectURL(photo) : photo}
                      alt={`Photo avant intervention ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleDeleteBeforePhoto(index)}
                        className="p-2 bg-error text-white rounded-full hover:bg-error/90 transition-colors"
                        title="Supprimer cette photo"
                        disabled={isSaving || isCompleting || uploading}
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    </div>
                    
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-xs text-white truncate">
                        {getFileName(photo)}
                      </p>
                    </div>
                  </div>
                ))}
                
                {beforePhotos.length === 0 && (
                  <div className="col-span-full flex flex-col items-center justify-center py-12 bg-muted/50 rounded-lg border-2 border-dashed border-border">
                    <Icon name="Camera" size={48} className="text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground font-medium">Aucune photo avant</p>
                    <p className="text-xs text-muted-foreground mt-1">Ajoutez des photos avant l'intervention</p>
                    <label className="cursor-pointer mt-4">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => handlePhotoUpload('before', e)}
                        className="hidden"
                        disabled={isSaving || isCompleting || uploading}
                      />
                      <Button variant="outline" size="sm" asChild disabled={isSaving || isCompleting || uploading}>
                        <span>
                          <Icon name="Plus" size={16} className="mr-2" />
                          Ajouter des photos
                        </span>
                      </Button>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Photos Après */}
           

            <div className="p-3 bg-info/10 border border-info/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-info mt-0.5" />
                <div className="text-xs text-info">
                  <p className="font-medium mb-1">Photos requises pour finaliser l'intervention</p>
                  <p>Au moins une photo avant est nécessaires pour terminer l'intervention.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Consumables Step */}
        {activeStep === 2 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Consommables utilisés</h3>
              <Button 
                variant="outline" 
                size="sm" 
                iconName="Plus" 
                onClick={() => setIsConsumableModalOpen(true)}
                disabled={isSaving || isCompleting || uploading}
              >
                Ajouter
              </Button>
            </div>
            
            {consumables.length === 0 ? (
              <div className="text-center py-8 bg-muted/50 rounded-lg">
                <Icon name="Package" size={32} className="text-muted-foreground mb-2 mx-auto" />
                <p className="text-sm text-muted-foreground">Aucun consommable ajouté</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Cliquez sur "Ajouter" pour sélectionner des consommables
                </p>
              </div>
            ) : (
              <>
                {consumables.map(item => {
                  // Récupérer la quantité disponible depuis le stockMap
                  const availableQuantity = stockMap.get(item.id) || 0;
                  const currentQuantity = item.quantity || 0;
                  
                  // Vérifier si la quantité dépasse la quantité disponible
                  const hasStockIssue = currentQuantity > availableQuantity;
                  
                  return (
                    <div key={item.id} className="flex flex-col p-3 bg-muted/50 rounded-lg group border border-border">
                      {/* Ligne supérieure avec infos */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-foreground">{item.name}</p>
                            {/* Badge de stock disponible - TOUJOURS AFFICHÉ avec la vraie valeur du stock */}
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              hasStockIssue 
                                ? 'bg-error/10 text-error' 
                                : 'bg-primary/10 text-primary'
                            }`}>
                              <Icon name="Package" size={10} className="inline mr-1" />
                              Stock: {availableQuantity} {item.unit || 'pcs'}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Unité: {item.unit || 'pcs'}  
                          </p>
                        </div>
                        
                        {/* Bouton supprimer */}
                        <button
                          onClick={() => handleRemoveConsumable(item.id)}
                          className="p-2 text-muted-foreground hover:text-error transition-colors"
                          title="Supprimer"
                          disabled={isSaving || isCompleting || uploading}
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                      
                      {/* Ligne inférieure avec contrôle de quantité */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          Quantité utilisée:
                        </span>
                        
                        <div className="flex items-center gap-2">
                          {/* Bouton - */}
                          <button
                            onClick={() => handleDecrementQuantity(item.id)}
                            className={`w-8 h-8 flex items-center justify-center rounded-l-lg border border-border ${
                              currentQuantity <= 0
                                ? 'bg-muted/30 text-muted-foreground cursor-not-allowed'
                                : 'bg-muted hover:bg-muted/80 text-foreground'
                            }`}
                            disabled={isSaving || isCompleting || uploading || currentQuantity <= 0}
                          >
                            <Icon name="Minus" size={16} />
                          </button>
                          
                          {/* Affichage de la quantité */}
                          <div className="w-16 text-center">
                            <span className={`text-base font-semibold ${
                              hasStockIssue ? 'text-error' : 'text-foreground'
                            }`}>
                              {currentQuantity}
                            </span>
                          </div>
                          
                          {/* Bouton + */}
                          <button
                            onClick={() => handleIncrementQuantity(item.id, availableQuantity)}
                            className={`w-8 h-8 flex items-center justify-center rounded-r-lg border border-border ${
                              currentQuantity >= availableQuantity
                                ? 'bg-muted/30 text-muted-foreground cursor-not-allowed'
                                : 'bg-muted hover:bg-muted/80 text-foreground'
                            }`}
                            disabled={
                              isSaving || 
                              isCompleting || 
                              uploading || 
                              currentQuantity >= availableQuantity
                            }
                          >
                            <Icon name="Plus" size={16} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Message d'erreur si dépassement */}
                      {hasStockIssue && (
                        <div className="mt-2 text-xs text-error bg-error/10 p-2 rounded">
                          <Icon name="AlertCircle" size={12} className="inline mr-1" />
                          Quantité ({currentQuantity}) dépasse le stock disponible ({availableQuantity})
                        </div>
                      )}
                      
                      {/* Barre de progression visuelle du stock */}
                      {availableQuantity > 0 && (
                        <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-300 ${
                              hasStockIssue ? 'bg-error' : 'bg-primary'
                            }`}
                            style={{ 
                              width: `${Math.min((currentQuantity / availableQuantity) * 100, 100)}%` 
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
            
            {/* Message d'information sur les stocks */}
            {consumables.length > 0 && (
              <div className="p-3 bg-info/10 border border-info/20 rounded-lg mt-4">
                <div className="flex items-start gap-2">
                  <Icon name="Info" size={16} className="text-info mt-0.5" />
                  <div className="text-xs text-info">
                    <p className="font-medium">Gestion des stocks</p>
                    <p>Les quantités ne peuvent pas dépasser le stock disponible. Utilisez les boutons + et - pour ajuster.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Signature Step */}
        {activeStep === 3 && (
          
          <div className="space-y-4">
             <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-foreground">Photos après intervention</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {afterPhotos.length} photo{afterPhotos.length > 1 ? 's' : ''}
                    {afterPhotos.length > 0 && ' · Requis *'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {afterPhotos.length > 0 && (
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      iconName="Trash2"
                      onClick={handleDeleteAllAfterPhotos}
                      disabled={isSaving || isCompleting || uploading}
                    >
                      Tout supprimer
                    </Button>
                  )}
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handlePhotoUpload('after', e)}
                      className="hidden"
                      disabled={isSaving || isCompleting || uploading}
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      iconName="Plus" 
                      asChild 
                      disabled={isSaving || isCompleting || uploading}
                    >
                      <span>Ajouter</span>
                    </Button>
                  </label>
                </div>
              </div>
              
              {/* Grille des photos après */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {afterPhotos.map((photo, index) => (
                  <div key={index} className="relative group aspect-square rounded-lg overflow-hidden bg-muted border border-border">
                    <Image
                      src={photo instanceof File ? URL.createObjectURL(photo) : photo}
                      alt={`Photo après intervention ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleDeleteAfterPhoto(index)}
                        className="p-2 bg-error text-white rounded-full hover:bg-error/90 transition-colors"
                        title="Supprimer cette photo"
                        disabled={isSaving || isCompleting || uploading}
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    </div>
                    
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-xs text-white truncate">
                        {getFileName(photo)}
                      </p>
                    </div>
                  </div>
                ))}
                
                {afterPhotos.length === 0 && (
                  <div className="col-span-full flex flex-col items-center justify-center py-12 bg-muted/50 rounded-lg border-2 border-dashed border-border">
                    <Icon name="Camera" size={48} className="text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground font-medium">Aucune photo après</p>
                    <p className="text-xs text-muted-foreground mt-1">Ajoutez des photos après l'intervention</p>
                    <label className="cursor-pointer mt-4">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => handlePhotoUpload('after', e)}
                        className="hidden"
                        disabled={isSaving || isCompleting || uploading}
                      />
                      <Button variant="outline" size="sm" asChild disabled={isSaving || isCompleting || uploading}>
                        <span>
                          <Icon name="Plus" size={16} className="mr-2" />
                          Ajouter des photos
                        </span>
                      </Button>
                    </label>
                  </div>
                )}
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-3">Signature du client</h3>
              <div className="border-2 border-dashed border-border rounded-lg p-4 bg-muted/30">
                {!signature ? (
                  <SignaturePad
                    onSave={handleSignatureComplete}
                    onCancel={() => setActiveStep(2)}
                    disabled={isSaving || isCompleting || uploading}
                  />
                ) : (
                  <div className="space-y-3">
                    <div className="bg-card rounded-lg p-4 border border-border relative group">
                      <div className="flex items-center justify-center py-8">
                        <img 
                          src={signature} 
                          alt="Signature du client" 
                          className="max-h-32 object-contain"
                        />
                      </div>
                      <p className="text-center text-sm text-muted-foreground">
                        Signature capturée le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => {
                            setSignature(null);
                            if (onUpdateSignature) {
                              onUpdateSignature(null);
                            }
                          }}
                          className="p-2 bg-error text-white rounded-full hover:bg-error/90 transition-colors"
                          title="Supprimer la signature"
                          disabled={isSaving || isCompleting || uploading}
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        iconName="RefreshCw"
                        onClick={() => {
                          setSignature(null);
                          if (onUpdateSignature) {
                            onUpdateSignature(null);
                          }
                        }}
                        className="flex-1"
                        disabled={isSaving || isCompleting || uploading}
                      >
                        Refaire la signature
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-primary mt-0.5" />
                <div className="text-xs text-primary">
                  <p className="font-medium mb-1">Garantie de 6 mois</p>
                  <p>Le client recevra automatiquement le certificat de garantie par email après validation de l'intervention</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="p-4 border-t border-border">
        <div className="flex gap-3">
          <Button
            variant="default"
            iconName="CheckCircle"
            onClick={handleCompleteIntervention}
            disabled={!canComplete() || isSaving || isCompleting || uploading}
            className="flex-1"
            loading={isCompleting}
          >
            {isCompleting ? 'Finalisation...' : "Terminer l'intervention"}
          </Button>
        </div>
        {!canComplete() && (
          <div className="text-xs text-error text-center mt-2 space-y-1">
            <p>Complétez tous les points obligatoires pour terminer :</p>
            <ul className="text-left ml-4 list-disc">
              {checklist.filter(item => item.required && !item.checked).length > 0 && (
                <li>Tous les points de contrôle obligatoires</li>
              )}
              {beforePhotos.length === 0 && <li>Photos avant intervention</li>}
              {afterPhotos.length === 0 && <li>Photos après intervention</li>}
              {!signature && <li>Signature du client</li>}
            </ul>
          </div>
        )}
        {uploading && (
          <div className="text-xs text-info text-center mt-2">
            <Icon name="Loader" size={12} className="inline animate-spin mr-1" />
            Upload des images en cours...
          </div>
        )}
      </div>
    </div>
  );
};

export default InterventionDetails;